import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, MessageSquare, UserPlus, RefreshCw, CheckCircle2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { es } from "date-fns/locale";

const ESTADOS_LABELS = {
  // nuevos (4 columnas Kanban)
  nuevo: "Nuevo",
  respondido: "En Curso",
  presupuesto_enviado: "Presupuesto Enviado",
  cerrado: "Cerrado",
  // legacy (compatibilidad histórica en historial_estados)
  contactado: "Contactado",
  pendiente_respuesta: "Pendiente Respuesta",
  cerrado_ganado: "Cerrado Ganado",
  cerrado_perdido: "Cerrado Perdido",
  nuevo_pedido: "Nuevo Pedido",
};

export default function Timeline({ lead }) {
  // Combinar todos los tipos de eventos disponibles en el lead
  const events = [
    ...(lead.historial_estados || []).map(e => ({ 
      ...e, 
      type: 'estado', 
      icon: RefreshCw, 
      color: 'text-blue-500', 
      bg: 'bg-blue-100 dark:bg-blue-900/30' 
    })),
    ...(lead.comentarios || []).filter(c => !c.archivado).map((c, i) => ({ 
      ...c, 
      id: `comment-${i}`,
      type: 'comentario', 
      icon: MessageSquare, 
      color: 'text-indigo-500', 
      bg: 'bg-indigo-100 dark:bg-indigo-900/30' 
    })),
    ...(lead.historial_actividad || []).map((a, i) => ({ 
      ...a, 
      id: `act-${i}`,
      type: 'actividad', 
      icon: a.tipo === 'asignacion' ? UserPlus : Clock, 
      color: 'text-green-500', 
      bg: 'bg-green-100 dark:bg-green-900/30' 
    }))
  ];

  // Ordenar por fecha DESC
  events.sort((a, b) => new Date(b.fecha).getTime() - new Date(a.fecha).getTime());

  const getEventTitle = (event) => {
    switch (event.type) {
      case 'estado':
        return `Estado: ${ESTADOS_LABELS[event.estado] || event.estado}`;
      case 'comentario':
        return 'Nuevo comentario';
      case 'actividad':
        if (event.tipo === 'asignacion') {
          return `Lead reasignado`;
        }
        return 'Actividad';
      default:
        return 'Evento';
    }
  };

  const getEventDescription = (event) => {
    switch (event.type) {
      case 'comentario':
        return event.texto;
      case 'actividad':
        if (event.tipo === 'asignacion') {
          return `De ${event.de} → ${event.a}`;
        }
        return '';
      default:
        return '';
    }
  };

  return (
    <Card className="dark:bg-gray-900 dark:border-gray-800">
      <CardHeader className="pb-4">
        <CardTitle className="text-lg flex items-center gap-2 text-gray-900 dark:text-white">
          <Clock className="w-5 h-5 text-indigo-500" />
          Historial de Actividad
        </CardTitle>
      </CardHeader>
      <CardContent>
        {events.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 bg-gray-50 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-3">
               <Clock className="w-6 h-6 text-gray-300" />
            </div>
            <p className="text-gray-500 dark:text-gray-400 text-sm">Sin actividad registrada aún</p>
          </div>
        ) : (
          <div className="relative space-y-6 before:absolute before:inset-0 before:ml-5 before:-translate-x-px before:h-full before:w-0.5 before:bg-gradient-to-b before:from-gray-200 before:via-gray-200 before:to-transparent dark:before:from-gray-800 dark:before:via-gray-800">
            {events.map((event, index) => (
              <div key={event.id || index} className="relative flex items-start gap-4 group">
                <div className={`flex flex-shrink-0 items-center justify-center w-10 h-10 rounded-full border-4 border-white dark:border-gray-900 ${event.bg} z-10 shadow-sm transition-transform group-hover:scale-110`}>
                  <event.icon className={`w-4 h-4 ${event.color}`} />
                </div>
                <div className="flex-1 pt-1 ml-1">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 mb-1">
                    <p className="text-sm font-bold text-gray-900 dark:text-white">
                      {getEventTitle(event)}
                    </p>
                    <time className="text-[10px] font-medium text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-800 px-2 py-0.5 rounded-full border border-gray-100 dark:border-gray-700">
                      {event.fecha ? formatDistanceToNow(new Date(event.fecha), { addSuffix: true, locale: es }) : 'Recientemente'}
                    </time>
                  </div>
                  {getEventDescription(event) && (
                    <div className="text-xs text-gray-600 dark:text-gray-400 bg-gray-50/80 dark:bg-gray-800/40 p-3 rounded-xl mt-1.5 border border-gray-100 dark:border-gray-700/50 shadow-sm italic">
                      {getEventDescription(event)}
                    </div>
                  )}
                  <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-2 flex items-center gap-1.5 px-1">
                    <span className="w-1 h-1 rounded-full bg-gray-300 dark:bg-gray-600" />
                    por <span className="font-medium">{event.usuario}</span>
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}